/**
 * Accessory card renderer + its add-to-cart cluster.
 * Extracted verbatim from custom-products.js. renderAccessory resolves
 * addProductToCart / buildVariantOptionSelectors through typeof guards at
 * MODULE scope — the whole cluster must live in one module or accessory
 * add-to-cart silently degrades to a full-page POST (window fallback is
 * never set). Free identifiers showMessageBox / getAddToCartData /
 * onlineServiceUrl / itemInCart intentionally resolve via window at call
 * time — do not convert them to imports. (getCartId / getPageType /
 * buildOnlineServiceAnalyticsEventFile inside trackAccessoryClick are the
 * same pattern, typeof-guarded; window.buildOnlineServiceAnalyticsEventFile
 * is set by global.js.)
 */
import utils from '@bigcommerce/stencil-utils';
import { showAlertModal } from '../../global/modal';
import openSlideCart from '../../common/open-slide-cart';
import { getCartPageOvrPricing } from '../shipping/ovr-shipping';
import { fireAddToCart } from '../event/ga4-events';
import { getMotherShipSku } from '../event/productSkuMap';
import { getRecommenderContext } from '../event/recommender-context';

function buildVariantOptionSelectors(product) {
  // Navigate to the options within the first variant node
  const options = product.variants?.edges[0]?.node?.options?.edges;

  if (!options || options.length === 0) {
    return null; // No options to build
  }

  const optionsContainer = document.createElement('div');
  // Add a wrapper div similar to the example for styling consistency
  optionsContainer.id = `data-product-option-change-${product.entityId}`;
  optionsContainer.setAttribute('data-product-option-change', '');
  optionsContainer.style.display = 'none';
  options.forEach(({ node: option }) => {
    const optionWrapper = document.createElement('div');
    optionWrapper.classList.add('form-field');
    // We assume 'radio' as we cannot determine if it's a 'swatch' from the query
    optionWrapper.setAttribute('data-product-attribute', 'radio');

    const mainLabel = document.createElement('label');
    mainLabel.classList.add('form-label', 'form-label--alternate', 'form-label--inlineSmall');

    // As we don't know the default, we'll pick the first one's label to display initially.
    const initialLabelText = option.values.edges[0]?.node.label || '';

    mainLabel.innerHTML = `
            ${option.displayName}:
            <span data-option-value="">${initialLabelText}</span>
            <small class="required-label">Required</small>
        `;
    optionWrapper.appendChild(mainLabel);

    // Create the radio inputs for each value
    option.values.edges.forEach(({ node: value }, index) => {
      const optionId = option.entityId;
      const valueId = value.entityId;
      // Use a unique prefix for the ID to avoid conflicts on the page
      const inputId = `acc_attribute_radio_${optionId}_${valueId}`;

      const input = document.createElement('input');
      input.classList.add('form-radio');
      input.type = 'radio';
      input.name = `attribute[${optionId}]`;
      input.id = inputId;
      input.value = valueId;
      input.required = true; // Assume required to prevent cart errors

      // Set the first option as checked by default
      if (index === 0) {
        input.checked = true;
      }

      // This label is for the radio button itself
      const valueLabel = document.createElement('label');
      valueLabel.classList.add('form-option');
      valueLabel.setAttribute('for', inputId);
      valueLabel.setAttribute('data-product-attribute-value', valueId);

      // We create a simple text-based label since we don't have swatch images.
      valueLabel.innerHTML = `<span class="form-option-variant">${value.label}</span>`;

      optionWrapper.appendChild(input);
      optionWrapper.appendChild(valueLabel);

      // Add event listener to update the main label text on change
      input.addEventListener('change', (event) => {
        if (event.target.checked) {
          const topLabelSpan = event.target.closest('.form-field').querySelector('[data-option-value]');
          if (topLabelSpan) {
            topLabelSpan.textContent = value.label;
          }
        }
      });
    });

    optionsContainer.appendChild(optionWrapper);
  });

  return optionsContainer;
}

function renderAccessory(accEl, product, displaySku, orderIndex, log) {
  const variantsEdges = product?.variants.edges ?? [];
  const firstVariant = variantsEdges[0]?.node ?? null;
  console.debug("First Variant: ", firstVariant);
  // Price
  const priceValue = firstVariant?.prices?.price?.value ?? product?.prices?.price?.value ?? null;
  const priceFormatted =
    firstVariant?.prices?.price?.formattedV2 ??
    product?.prices?.price?.formattedV2 ??
    (priceValue != null ? `$${priceValue}` : '');

  // Form SKU
  let formSku = firstVariant?.sku ?? product?.sku ?? displaySku;
  if (typeof formSku === 'string' && formSku.endsWith('BC')) {
    formSku = formSku.slice(0, -2);
  }

  // Image URL
  const imageUrl = firstVariant?.defaultImage?.url ?? product?.defaultImage?.url ?? '';

  // CTA form
  const entityId = product?.entityId;
  const cta = document.createElement('form');
  cta.action = `/cart.php?action=add&sku=${encodeURIComponent(formSku)}`;
  cta.enctype = 'multipart/form-data';
  cta.setAttribute('data-cart-item-add', '');

  const hidden = document.createElement('input');
  hidden.name = 'product_id';
  hidden.type = 'hidden';
  hidden.value = entityId;

  const submit = document.createElement('input');
  submit.type = 'submit';
  submit.setAttribute('ss-track-cart-add', entityId);
  submit.classList.add('accessory__cta');
  submit.value = 'Add to cart';
  // Starts with the visible label (WCAG 2.5.3) so voice control still matches "Add to cart".
  submit.setAttribute('aria-label', `Add to cart: ${product?.name ?? displaySku}`);
  submit.setAttribute('data-product-id', entityId);

  cta.appendChild(hidden);
  cta.appendChild(submit);

  if (typeof addProductToCart === 'function') {
    cta.addEventListener('submit', (event) => addProductToCart(event, cta));
  } else {
    log.warn('addProductToCart(event, form) not found—submit will do normal POST');
  }

  // Optional variant selectors
  if (typeof buildVariantOptionSelectors === 'function') {
    try {
      const optionSelectors = buildVariantOptionSelectors(product);
      if (optionSelectors) cta.appendChild(optionSelectors);
    } catch (e) {
      log.warn('buildVariantOptionSelectors threw', e);
    }
  }

  // DOM structure
  const wrap = document.createElement('div');
  wrap.classList.add('accessory');
  wrap.style.order = String(orderIndex + 1);
  if (entityId != null) wrap.setAttribute('data-entity-id', entityId);

  const text = document.createElement('div');
  const linkedText = document.createElement('a');
  linkedText.classList.add('accessory__link');

  const title = document.createElement('div');
  title.classList.add('accessory__title');
  title.innerText = product?.name ?? displaySku;

  const skuDiv = document.createElement('div');
  skuDiv.classList.add('accessory__sku');
  skuDiv.innerText = `SKU: ${displaySku}`;

  const price = document.createElement('div');
  price.classList.add('accessory__price');
  // Format price as currency if it's a number
  if (priceFormatted && !priceFormatted.startsWith('$')) {
    const numPrice = parseFloat(priceFormatted);
    price.innerText = !isNaN(numPrice) ? `$${numPrice.toFixed(2)}` : priceFormatted;
  } else {
    price.innerText = priceFormatted;
  }

  // The image link duplicates the title link, so it stays mouse-only: hidden from screen
  // readers and the Tab order, with a decorative image.
  const thumb = document.createElement('a');
  thumb.classList.add('accessory__image');
  thumb.setAttribute('aria-hidden', 'true');
  thumb.tabIndex = -1;

  const thumbImg = document.createElement('img');
  thumbImg.alt = '';
  thumbImg.src = imageUrl || '';
  thumb.appendChild(thumbImg);

  const href = product?.path ? `${product.path}?sku=${encodeURIComponent(formSku)}` : null;
  if (href) {
    thumb.href = href;
    linkedText.href = href;
  }

  linkedText.appendChild(title);
  linkedText.appendChild(skuDiv);
  text.appendChild(linkedText);
  text.appendChild(price);
  text.appendChild(cta);

  wrap.appendChild(thumb);
  wrap.appendChild(text);

  // Append to container
  accEl.appendChild(wrap);

  // Add click tracking for accessory interactions
  function trackAccessoryClick(type, el) {
    const sku = displaySku; // We already have the SKU from the function params
    console.log("Track accessory event:", { type, sku, href: el.href || null });

    const cartId = typeof getCartId === "function" ? getCartId() : null;
    const pageType = typeof getPageType === "function" ? getPageType() : "unknown";

    let newEvent = {
      loadNumber: 1,
      scriptLoaded: null,
      interactionsCompleted: null,
      errorMessage: {
        eventName: type,
        eventMessage: "",
        timeStamp: new Date().toISOString(),
      },
    };

    let quantity = null;
    if (type === "add_to_cart_start") {
      quantity = 1; // Accessories default to quantity 1
    }

    let eventObject = {
      tealium_event: type,
      sku: sku,
      href: el.href || null,
      page_type: pageType,
      recommender: "accessories",
      recommenderName: "Recommended Accessories",
      quantity: quantity,
    };

    if (typeof buildOnlineServiceAnalyticsEventFile === "function") {
      buildOnlineServiceAnalyticsEventFile(newEvent, eventObject, cartId);
    }
  }

  // Add event listeners to the created elements
  if (thumb && thumb.href) {
    thumb.addEventListener("click", (e) => {
      trackAccessoryClick("image_click", e.currentTarget);
    });
  }

  if (linkedText && linkedText.href) {
    linkedText.addEventListener("click", (e) => {
      trackAccessoryClick("name_click", e.currentTarget);
    });
  }

  if (submit) {
    submit.addEventListener("click", (e) => {
      trackAccessoryClick("add_to_cart_start", e.currentTarget);
    });
  }
}

function updateSideCart() {
  const $sideCart = $('.side-cart-content');
  const $sideCartDetails = $('.side-cart-details');
  const $sideCartActions = $('.side-cart-actions');

  const options = {
    template: {
      template: 'f/b2b/side-cart-content',
      details: 'f/b2b/side-cart-details',
      actions: 'f/b2b/side-cart-actions',
      itemcount: 'f/cart/item-count',
    },
  };

  utils.api.cart.getContent(options, (err, response) => {
    if (response?.itemcount > 0) {
      //console.log("getContent response form: ",response);
      $sideCart.html(response.template);
      $sideCartDetails.html(response.details);
      $sideCartActions.html(response.actions);
      $('.cart-quantity').html(response.itemcount);
      itemInCart();
    }

    $('.side-cart-loading').css('display', 'none');
    $('.side-cart-item').css('opacity', '1');
  });
}
function filterEmptyFilesFromForm(formData) {
  try {
    for (const [key, val] of formData) {
      if (val instanceof File && !val.name && !val.size) {
        formData.delete(key);
      }
    }
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
  }
  return formData;
}
function addProductToCart(event, form) {
  // const previewModal = modalFactory('#previewModal')[0];
  // const quickviewModal = modalFactory('#modal')[0];
  //const messageBoxTimer = setTimeout(() => { }, 0);
  const $addToCartBtn = $('#form-action-addToCart', $(event.target));
  const originalBtnVal = $addToCartBtn.val();
  const waitMessage = $addToCartBtn.data('waitMessage');

  const qtyInput = $(event.target).closest('.form').find('.form-input--incrementTotal') || 1;
  const qtyMin = parseInt(qtyInput.attr('data-quantity-min'), 10) || null;
  const qtyMax = parseInt(qtyInput.attr('data-quantity-max'), 10) || null;
  const minErr = qtyInput.attr('data-quantity-min-error') || null;
  const maxErr = qtyInput.attr('data-quantity-max-error') || null;
  const newQty = parseInt(Number(qtyInput.val()), 10) || 0;

  const $atcAction = $('#atc-form-action');
  $atcAction.val('add');

  // Do not do AJAX if browser doesn't support FormData
  if (window.FormData === undefined) {
    return;
  }

  // Prevent default
  event.preventDefault();

  const recommenderContext = getRecommenderContext(form);
  const skuDataElement = form.querySelector('input[type="hidden"][name="product_id"]');
  // if(skuDataElement){
  //     const skuData = skuDataElement.value;
  //     const qtyDataElement = form.querySelector('input[id="qty[]"][name="qty[]"]');
  //     let qtyData = 1;
  //     if(qtyDataElement){
  //         qtyData  = qtyDataElement.value;
  //     }
  //     console.log("qtyInput: ", qtyInput);
  //     getAddToCartData(skuData,qtyData);
  // }

  $addToCartBtn
    .val(waitMessage)
    .prop('disabled', true);

  // Does not quality for min/max quantity
  if (newQty < qtyMin) {
    $addToCartBtn
      .val(originalBtnVal)
      .prop('disabled', false);

    showMessageBox(minErr, 4000);
    // swal({
    //     text: minErr,
    //     type: 'error',
    // }).catch(swal.noop);
  } else if (qtyMax > 0 && newQty > qtyMax) {
    $addToCartBtn
      .val(originalBtnVal)
      .prop('disabled', false);

    showMessageBox(maxErr, 4000);
    // swal({
    //     text: maxErr,
    //     type: 'error',
    // }).catch(swal.noop);
  } else if (newQty < 0 || Number.isNaN(newQty)) {
    $addToCartBtn
      .val(originalBtnVal)
      .prop('disabled', false);

    const invalidEntry = qtyInput.val();
    qtyInput.val(qtyMin);

    showMessageBox(`${invalidEntry} is not a valid entry`, 4000);
    // swal({
    //     text: `${invalidEntry} is not a valid entry`,
    //     type: 'error',
    // })
    //     .catch(swal.noop);
  } else {
    //this.$overlay.show();

    // Add item to cart
    utils.api.cart.itemAdd(filterEmptyFilesFromForm(new FormData(form)), (err, response) => {
      const errorMessage = err || response.data.error;

      //this.$overlay.hide();

      $addToCartBtn
        .val(originalBtnVal)
        .prop('disabled', false);

      // Guard statement
      if (errorMessage) {
        // Strip the HTML from the error message
        const tmp = document.createElement('DIV');
        tmp.innerHTML = errorMessage;

        return showAlertModal(tmp.textContent || tmp.innerText);
      }

      //if (quickviewModal) {
      //    quickviewModal.close();
      //}

      // Open preview modal and update content
      // ******* CHANGED FROM POPUP MODAL TO SLIDEOUT CART - 6.14.24 WTK ***********
      // if (this.previewModal) {
      //     if (this.context.addToCartMode === 'popup') {
      //         this.previewModal.open();
      //     }

      //     this.updateCartContent(this.previewModal, response.data.cart_item.id);
      // } else {
      //     this.$overlay.show();
      //     // if no modal, redirect to the cart page
      //     this.redirectTo(response.data.cart_item.cart_url || this.context.urls.cart);
      // }

      getAddToCartData(response, null, null, null, recommenderContext);

      // GA4 add_to_cart — same line_items enrichment as card-add-to-cart.js;
      // response.data.cart_item alone lacks category_names / brand / pricing.
      if (response.data?.cart_item) {
        const cartData = response.data;
        const lineItem = (cartData.line_items || [])
          .find(li => li.product_id === cartData.cart_item?.product_id)
          || cartData.line_items?.[0]
          || {};
        const enrichedCartItem = {
          ...cartData.cart_item,
          name:           lineItem.product_name || cartData.cart_item.name || '',
          product_sku:    getMotherShipSku(cartData.cart_item?.product_id) || lineItem.sku || cartData.cart_item.sku || '',
          sku:            lineItem.sku || cartData.cart_item.sku || '',
          sale_price:     lineItem.sale_price ?? cartData.cart_item.sale_price
                              ?? cartData.cart_item.price_ex_tax
                              ?? cartData.product_value ?? 0,
          base_price:     lineItem.base_price ?? null,
          retail_price:   lineItem.retail_price ?? null,
          purchase_price: lineItem.purchase_price ?? null,
          quantity:       lineItem.quantity ?? cartData.cart_item.quantity ?? 1,
          discount:       lineItem.discount ?? 0,
          brand_name:     lineItem.brand_name || '',
          category_names: lineItem.category_names || [],
          variant_id:     lineItem.variant_id ?? null,
          currency:       lineItem.currency || cartData.currency || 'USD',
          price:          lineItem.purchase_price ?? 0,
        };
        fireAddToCart(enrichedCartItem, enrichedCartItem.currency, recommenderContext?.recommenderName);
      }

      utils.api.cart.getCart({}, (err, response) => {
        const sendURL = onlineServiceUrl ?? 'https://api.storesupply.com/';
        const tru = true;
        if (response) {
          let happens = getCartPageOvrPricing(response?.lineItems?.physicalItems, sendURL, tru);
        }
        else {
          let happens = getCartPageOvrPricing(null, sendURL, tru);
        }
      });
      const sendURL = onlineServiceUrl ?? 'https://api.storesupply.com/';
      const tru = true;
      if (response) {
        console.debug('productdetails cartItems: ', response.data.line_items);
        let happens = getCartPageOvrPricing(response.data.line_items, sendURL, tru);

      }
      else {
        let happens = getCartPageOvrPricing(null, sendURL, tru);
      }
      //updateSideCart(previewModal, response.data.cart_item.id);
      document.prevFocus = (document.activeElement || document.body);
      updateSideCart();
      openSlideCart();
    });
  }
}

export { renderAccessory };
